// PrepareOrderApp.tsx
import React, { useState, useEffect, useRef } from "react";
import { Product, SelectedProduct } from "./types";
import OrderHeader from "./OrderHeader";
import PrescriptionCard, {
  PrescriptionCardHandle,
  PrescriptionCardWithRef,
} from "./PrescriptionCard";
import DeliveryInfo from "./DeliveryInfo";
import ProductsSection from "./ProductsSection";
import { PrescriptionFormData } from "../OrderInfo/OrderDetails/GeneratePrescriptionForm";
import { Order } from "../OrderInfo/types";
import PrescriptionImage from "../OrderInfo/PrescriptionImage";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import { OrderService } from "../../../../../services/api/order.service";

interface PrepareOrderProps {
  onExit: () => void;
  prescriptionImageData: string | null;
  prescriptionFormData: PrescriptionFormData | null;
  selectedOrder: Order | null;
  fetchOrder: () => void;
}

const PrepareOrder: React.FC<PrepareOrderProps> = ({
  onExit,
  prescriptionFormData,
  selectedOrder,
  fetchOrder,
}) => {
  const prescriptionCardRef = useRef<PrescriptionCardHandle>(null);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
  });
  const [isDoctorRequired,setIsDoctorRequired]=useState(false);
  const [doctorName,setDoctorName]=useState<string|undefined>();
  
    useEffect(()=>{
      const checkDocNameRequired=async()=>{
        if(selectedOrder?.id){
        const check=await OrderService.checkOrder(selectedOrder?.id);
        setIsDoctorRequired(check);
        }
      }
      checkDocNameRequired();
    },[selectedOrder?.id])


 const fetchProducts = async (searchTerm = "", page = 1, itemsPerPage = 10) => {
    try {
      setLoading(true);
      setError(null);

      const response = await AdminOrderService.getClinicProductListing(
        page,
        itemsPerPage,
        searchTerm,
        ""
      );

      if (response?.data?.data && Array.isArray(response.data.data)) {
        const transformedProducts = response.data.data.map((item) => ({
          compositeId: `${item.id}_${item.product_id}`,
          id: item.id,
          product_id: item.product_id,
          product_name: item.product?.name || "Unknown Product",
          manufacturer_name: item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
          product_image: item.product?.productImage || medicineIcon,
          landing_cost: parseFloat(item.landing_cost) || 0,
          mrp: parseFloat(item.mrp) || 0,
          quantity: item.no_of_items || item.remaining_stocks || 0,
          expiry_date: item.expiry_date,
          batch_number: item.batch_number,
          hsn_number: item.hsn_number,
          product_rate: parseFloat(item.product_rate) || 0,
          discount: parseFloat(item.discount) || 0,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          free_items: item.free_items || 0,
          finalSP: parseFloat(item.finalSP) || 0,
          sellingDiscount: parseFloat(item.sellingDiscount),
          remainingStocks: item.remaining_stocks,
          salt:item.salt??"N/A"
        }));

        console.log("API Response:", response.data);
        console.log("Transformed Products:", transformedProducts);

        setAvailableProducts(transformedProducts);
        setPagination({
          currentPage: page,
          itemsPerPage,
          totalItemsCount: response.data.pagination?.totalItemsCount || 0,
          totalPages: response.data.pagination?.totalPages || 1,
        });
      } else {
        throw new Error("Invalid products data format - expected array in response.data.data");
      }
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(searchText, pagination.currentPage, pagination.itemsPerPage);
  }, []);

  const handleSearch = (searchTerm: string) => {
    setSearchText(searchTerm);
    fetchProducts(searchTerm, 1, pagination.itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
      fetchProducts(searchText, page, pagination.itemsPerPage);
    }
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination((prev) => ({ ...prev, itemsPerPage, currentPage: 1 }));
    fetchProducts(searchText, 1, itemsPerPage);
  };

  const handleAddProduct = async (product: Product) => {
  const existingProduct = selectedProducts.find(
    (p) => p.compositeId === product.compositeId
  );
  

  if (existingProduct) {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.compositeId === product.compositeId
          ? { ...p, selectedQuantity: p.selectedQuantity + 1 }
          : p
      )
    );
  } else {
    setSelectedProducts((prev) => [
      ...prev,
      { ...product, selectedQuantity: 1 },
    ]);
  }
};

// Update quantity change logic
const handleUpdateQuantity = (compositeId: string, quantity: number) => {
  if (quantity <= 0) {
    handleRemoveProduct(compositeId);
    return;
  }

  setSelectedProducts((prev) =>
    prev.map((p) =>
      p.compositeId === compositeId 
        ? { ...p, selectedQuantity: quantity } 
        : p
    )
  );
};

// Update remove product logic
const handleRemoveProduct = (compositeId: string) => {
  setSelectedProducts((prev) =>
    prev.filter((p) => p.compositeId !== compositeId)
  );
};

  const renderPrescriptionComponent = () => {
    if (prescriptionFormData) {
      return (
        <PrescriptionCardWithRef
          ref={prescriptionCardRef}
          patientId={selectedOrder?.patientId}
          formData={prescriptionFormData}
        />
      );
    } else if (selectedOrder?.hasPrescription) {
      // Get all prescription images
      const images =
        selectedOrder.prescriptionDetails?.flatMap((p) =>
          Array.isArray(p.prescriptionImage)
            ? p.prescriptionImage
            : [p.prescriptionImage]
        ) || [];

      return images.length > 0 ? (
        <PrescriptionImage imageUrls={images} />
      ) : null;
    }
    return null;
  };

  const productsSectionPagination = {
    currentPage: pagination.currentPage,
    itemsPerPage: pagination.itemsPerPage,
    totalItemsCount: pagination.totalItemsCount,
    totalPages: pagination.totalPages,
    nextPage: pagination.currentPage < pagination.totalPages ? pagination.currentPage + 1 : null,
    previousPage: pagination.currentPage > 1 ? pagination.currentPage - 1 : null,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <OrderHeader onClose={onExit} />

        <div className="grid grid-cols-3 gap-2 w-full">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">
                Order Details
              </h3>
              {renderPrescriptionComponent()}
              <DeliveryInfo
                selectedProducts={selectedProducts}
                onNotifyComplete={onExit}
                address={selectedOrder?.orderAddress}
                pincode={selectedOrder?.orderPincode}
                orderDate={selectedOrder?.orderDate}
                patientId={selectedOrder?.patientId}
                selectedOrder={selectedOrder}
                prescriptionCardRef={prescriptionCardRef}
                fetchOrder={fetchOrder}
                doctorName={doctorName}
                setIsDoctorRequired={setIsDoctorRequired}
                isDoctorRequired={isDoctorRequired}
              />
            </div>
          </div>

          {/* Right Column - Products */}
          <div className="lg:col-span-2">
            <div
              className="bg-white rounded-lg p-6 min-h-screen max-h-screen overflow-y-auto"
              style={{
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
              }}
            >
              <ProductsSection
                availableProducts={availableProducts}
                selectedProducts={selectedProducts}
                onAddProduct={handleAddProduct}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveProduct={handleRemoveProduct}
                selectedOrder={selectedOrder}
                loading={loading}
                error={error}
                onSearch={handleSearch}
                pagination={productsSectionPagination}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                isDoctorRequired={isDoctorRequired}
                doctorName={doctorName}
                setDoctorName={setDoctorName}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrepareOrder;
